import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, Link } from 'react-router-dom'
import moment from 'moment'
import { Card, Container, Row, Col, Form, FloatingLabel, Button, Alert } from 'react-bootstrap'
import axios from 'axios'
import { parsePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input'

import 'react-phone-number-input/style.css'
import {
  listUserBasicDetails,
  listUserPrimeDetails,
  listUserPrimePlusDetails,
  updateUserPrimeDetails,
  updateUserPrimePlusDetails,
  updateUserMandatoryDetails,
  updateUserPassword,
  clearBasicDetailsMessage,
  clearUpdatePasswordMessage,
  clearUpdatePrimeDetailsMessage,
  clearUpdatePrimePlusDetailsMessage,
  updateUserEmail,
  clearUpdateEmailMessage,
  updateUserCity,
  clearUpdateCityMessage,
} from '../../actions/userActions'
import PhoneInput from '../../components/Shared/MUIComponents/PhoneInput'
import withLoginRequired from '../../HOC/LoginModal'
import { UPDATE_PRIME_DETAILS_FAIL } from '../../constants/userConstants'
import AutoSuggest from '../../components/Autosuggest'
import { APIUserCity, APIParentsData, APIParent1Data, APIParent2Data } from '../../utils/config/API'

const emptyParent = {
  id: null,
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  country: 'IN',
}

const normalizeEmail = (value = '') =>
  String(value)
    .normalize('NFKC')
    .replace(/[\u180E\u200B-\u200F\u202A-\u202E\u2060-\u206F\uFEFF]/g, '')
    .replace(/\u00A0/g, ' ')
    .trim()

const isValidCleanEmail = (value) => {
  const cleanedEmail = normalizeEmail(value)
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  return emailPattern.test(cleanedEmail)
}

const normalizePhoneNumber = (value, defaultCountry = 'IN') => {
  if (!value) {
    return ''
  }

  const rawValue = String(value).trim()

  // Keep only characters relevant to a phone number.
  const digits = rawValue.replace(/\D/g, '')

  if (!digits) {
    return ''
  }

  try {
    // Already an international number.
    if (rawValue.startsWith('+')) {
      return parsePhoneNumber(rawValue)?.number || rawValue
    }

    /*
     * Legacy DB format can contain:
     *
     *   918830827249
     *
     * which actually means:
     *
     *   +91 8830827249
     *
     * First check whether the raw digits already form a valid
     * international number when only "+" is missing.
     */
    const internationalCandidate = parsePhoneNumber(`+${digits}`)

    if (internationalCandidate && internationalCandidate.country === defaultCountry && internationalCandidate.isValid()) {
      return internationalCandidate.number
    }

    /*
     * Otherwise treat it as a genuine national number.
     *
     * Example:
     *   8830827249
     * becomes:
     *   +918830827249
     */
    const nationalCandidate = parsePhoneNumber(digits, defaultCountry)

    return nationalCandidate?.number || rawValue
  } catch (error) {
    return rawValue
  }
}

const getPhoneCountry = (value, defaultCountry = 'IN') => {
  if (!value) {
    return defaultCountry
  }

  try {
    return parsePhoneNumber(value)?.country || defaultCountry
  } catch (error) {
    return defaultCountry
  }
}

const ParentDetailsForm = ({
  title,
  parentNumber,
  parent,
  errors,
  status,
  updateParentField,
  onParentPhoneChange,
  onParentEmailBlur,
  saveParent,
}) => {
  return (
    <Card className="text-center edit-user-card-bg color-white mt-4">
      <Card.Body>
        <Card.Title>
          <h3 className="color-white">{title}</h3>
        </Card.Title>

        <Row>
          <Col xs={12} sm={12} md={6} className="mb-3">
            <FloatingLabel controlId={`parent${parentNumber}FirstName`} label="First Name" className="fs-6 color-light-pink">
              <Form.Control
                className="bg-grey color-white"
                type="text"
                value={parent.firstName}
                onChange={(e) => updateParentField(parentNumber, 'firstName', e.target.value)}
                placeholder="First Name"
              />
            </FloatingLabel>

            {errors.firstName ? <div className="text-danger mt-1">{errors.firstName}</div> : null}
          </Col>

          <Col xs={12} sm={12} md={6} className="mb-3">
            <FloatingLabel controlId={`parent${parentNumber}LastName`} label="Last Name" className="fs-6 color-light-pink">
              <Form.Control
                className="bg-grey color-white"
                type="text"
                value={parent.lastName}
                onChange={(e) => updateParentField(parentNumber, 'lastName', e.target.value)}
                placeholder="Last Name"
              />
            </FloatingLabel>

            {errors.lastName ? <div className="text-danger mt-1">{errors.lastName}</div> : null}
          </Col>
        </Row>

        <Row>
          <Col xs={12} sm={12} md={6} className="mb-3 parent-phone-input">
            <PhoneInput
              label="Phone Number"
              id={`parent${parentNumber}Phone`}
              name={`parent${parentNumber}Phone`}
              country={parent.country || 'IN'}
              value={parent.phone}
              onChange={onParentPhoneChange(parentNumber)}
              error={Boolean(errors.phone)}
              errorMessage={errors.phone || 'Please enter valid Phone Number'}
            />
          </Col>

          <Col xs={12} sm={12} md={6} className="mb-3">
            <FloatingLabel controlId={`parent${parentNumber}Email`} label="Email" className="fs-6 color-light-pink">
              <Form.Control
                className="bg-grey color-white"
                type="text"
                value={parent.email}
                onChange={(e) => updateParentField(parentNumber, 'email', e.target.value)}
                onBlur={() => onParentEmailBlur(parentNumber)}
                placeholder="Email"
                autoComplete="off"
              />
            </FloatingLabel>

            {errors.email ? <div className="text-danger mt-1">{errors.email}</div> : null}
          </Col>
        </Row>

        <Row className="m-0">
          <Col className="text-center mt-3">
            <Button type="button" disabled={status.loading || !parent.id} onClick={() => saveParent(parentNumber)}>
              {status.loading ? 'Updating...' : `Update ${title}`}
            </Button>
          </Col>
        </Row>

        {!parent.id ? (
          <Alert variant="warning" className="rounded mt-3">
            No linked {title} record exists for this student.
          </Alert>
        ) : null}

        {status.success ? (
          <Alert variant="success" className="rounded mt-3">
            {status.success}
          </Alert>
        ) : null}

        {status.error ? (
          <Alert variant="danger" className="rounded mt-3">
            {status.error}
          </Alert>
        ) : null}
      </Card.Body>
    </Card>
  )
}

export default withLoginRequired(() => {
  const userID = useParams().id

  const dispatch = useDispatch()
  const editUserDetails = useSelector((state) => state.editUserDetails)
  const editUserPrimeDetails = useSelector((state) => state.editUserPrimeDetails)
  const editUserPrimePlusDetails = useSelector((state) => state.editUserPrimePlusDetails)

  const updatePrimeReducer = useSelector((state) => state.updatePrimeReducer)
  const updatePrimePlusReducer = useSelector((state) => state.updatePrimePlusReducer)
  const updateMandatoryReducer = useSelector((state) => state.updateMandatoryReducer)

  const updatePassword = useSelector((state) => state.updatePassword)
  const updateEmail = useSelector((state) => state.updateEmail)
  const updateCity = useSelector((state) => state.updateCity)

  const currentDate = moment().format('YYYY-MM-DD')
  const currentNextYearDate = moment().add(1, 'years').format('YYYY-MM-DD')

  const [mandatoryData, setMandatoryData] = useState({
    userID,
    fName: '',
    lName: '',
    countryCode: '',
    phoneNumber: '',
    grade: '',
    curriculum: '',
    graduationYear: '',
    mobileOTP: '',
  })

  const { fName, lName, countryCode, phoneNumber, grade, curriculum, graduationYear, mobileOTP } = mandatoryData

  const [primeFields, setPrimeFields] = useState({
    prime: editUserPrimeDetails.userDetails.ISPREMIUM,
    primeStartDate: editUserPrimeDetails.userDetails.PREMIUMSTART,
    primeEndDate: editUserPrimeDetails.userDetails.PREMIUMEND,
  })

  const [primePlusFields, setPrimePlusFields] = useState([
    // {primePlusID: 1, startDate: currentDate, endDate: currentNextYearDate},
  ])

  const [password, setPassword] = useState('')

  const [showPrimePlusAlert, setShowPrimePlusAlert] = useState(false)
  const [showPrimeAlert, setShowPrimeAlert] = useState(false)
  const [showBasicAlert, setShowBasicAlert] = useState(false)

  const [showPasswordAlert, setShowPasswordAlert] = useState(false)

  const [passwordMinErrorText, setPasswordMinErrorText] = useState('')

  const [email, setEmail] = useState('')

  const [showEmailAlert, setShowEmailAlert] = useState(false)
  const [showCityAlert, setShowCityAlert] = useState(false)
  const [emailErrorText, setEmailErrorText] = useState('')

  const [parent1, setParent1] = useState(emptyParent)
  const [parent2, setParent2] = useState(emptyParent)

  const [parent1Errors, setParent1Errors] = useState({})
  const [parent2Errors, setParent2Errors] = useState({})

  const [parent1Status, setParent1Status] = useState({
    loading: false,
    success: '',
    error: '',
  })

  const [parent2Status, setParent2Status] = useState({
    loading: false,
    success: '',
    error: '',
  })

  useEffect(() => {
    const getParents = async () => {
      try {
        const { data } = await axios.get(`${APIParentsData}/${userID}`, {
          headers: {
            authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        })

        const parent1Data = data?.data?.parent1Details
        const parent2Data = data?.data?.parent2Details

        const parent1Phone = normalizePhoneNumber(parent1Data?.PHONE, 'IN')

        console.log('Parent 1 Phone:', parent1Phone, 'Parent 2 Phone:', parent2Data?.PHONE)
        const parent2Phone = normalizePhoneNumber(parent2Data?.PHONE, 'IN')

        setParent1(
          parent1Data
            ? {
                id: parent1Data.ID || null,
                firstName: parent1Data.FIRST_NAME || '',
                lastName: parent1Data.LAST_NAME || '',
                phone: parent1Phone,
                email: normalizeEmail(parent1Data.EMAIL || ''),
                country: getPhoneCountry(parent1Phone, 'IN'),
              }
            : { ...emptyParent }
        )

        setParent2(
          parent2Data
            ? {
                id: parent2Data.ID || null,
                firstName: parent2Data.FIRST_NAME || '',
                lastName: parent2Data.LAST_NAME || '',
                phone: parent2Phone,
                email: normalizeEmail(parent2Data.EMAIL || ''),
                country: getPhoneCountry(parent2Phone, 'IN'),
              }
            : { ...emptyParent }
        )
      } catch (err) {
        setParent1Status((state) => ({
          ...state,
          error: err.response?.data?.message || 'Unable to load parent details',
        }))
      }
    }

    getParents()
  }, [userID])
  console.log('Parent 1 Phone:', parent1.phone, 'Parent 2 Phone:', parent2.phone)

  const updateParentField = (parentNumber, field, value) => {
    const setter = parentNumber === 1 ? setParent1 : setParent2
    const errorSetter = parentNumber === 1 ? setParent1Errors : setParent2Errors

    setter((state) => ({
      ...state,
      [field]: value,
    }))

    errorSetter((state) => ({
      ...state,
      [field]: '',
    }))
  }

  // const onParentPhoneChange = (parentNumber) => (eventOrValue) => {
  //   let value = ''

  //   if (typeof eventOrValue === 'string') {
  //     value = eventOrValue
  //   } else if (eventOrValue?.target) {
  //     value = eventOrValue.target.value || ''
  //   }

  //   updateParentField(parentNumber, 'phone', value)
  // }
  const onParentPhoneChange = (parentNumber) => (name, value) => {
    updateParentField(parentNumber, 'phone', value || '')
  }
  const onParentEmailBlur = (parentNumber) => {
    const parent = parentNumber === 1 ? parent1 : parent2

    updateParentField(parentNumber, 'email', normalizeEmail(parent.email))
  }

  const isPhoneValid = (phone) => {
    if (!phone) {
      return false
    }

    try {
      return isValidPhoneNumber(phone)
    } catch (error) {
      return false
    }
  }

  const validateParent = (parent, parentNumber) => {
    const errors = {}

    const cleanedEmail = normalizeEmail(parent.email)

    if (parentNumber === 1) {
      if (!parent.firstName.trim()) {
        errors.firstName = 'First Name is required'
      }

      if (!parent.lastName.trim()) {
        errors.lastName = 'Last Name is required'
      }

      if (!cleanedEmail) {
        errors.email = 'Email is required'
      }

      if (!parent.phone) {
        errors.phone = 'Phone Number is required'
      }
    }

    if (cleanedEmail && !isValidCleanEmail(cleanedEmail)) {
      errors.email = 'Please enter a valid Email'
    }

    if (parent.phone && !isPhoneValid(parent.phone)) {
      console.log('Phone validation failed for', parent.phone, 'for parent', parentNumber)
      errors.phone = 'Please enter a valid Phone Number'
    }

    return {
      errors,
      cleanedEmail,
      isValid: Object.keys(errors).length === 0,
    }
  }

  const saveParent = async (parentNumber) => {
    const parent = parentNumber === 1 ? parent1 : parent2
    const setErrors = parentNumber === 1 ? setParent1Errors : setParent2Errors
    const setStatus = parentNumber === 1 ? setParent1Status : setParent2Status

    const endpoint = parentNumber === 1 ? APIParent1Data : APIParent2Data

    const { errors, cleanedEmail, isValid } = validateParent(parent, parentNumber)

    setErrors(errors)

    if (!isValid) {
      return
    }

    // Current backend cannot create an unlinked parent.
    if (!parent.id) {
      setStatus({
        loading: false,
        success: '',
        error: `Parent ${parentNumber} is not linked to this student`,
      })
      return
    }

    try {
      setStatus({
        loading: true,
        success: '',
        error: '',
      })

      const payload = {
        userID,
        parentFirstName: parent.firstName.trim(),
        parentLastName: parent.lastName.trim(),
        parentPhone: parent.phone,
        parentEmail: cleanedEmail,
        parentID: parent.id,
      }

      await axios.patch(endpoint, payload, {
        headers: {
          authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })

      if (parentNumber === 1) {
        setParent1((state) => ({
          ...state,
          firstName: state.firstName.trim(),
          lastName: state.lastName.trim(),
          email: cleanedEmail,
        }))
      } else {
        setParent2((state) => ({
          ...state,
          firstName: state.firstName.trim(),
          lastName: state.lastName.trim(),
          email: cleanedEmail,
        }))
      }

      setStatus({
        loading: false,
        success: `Parent ${parentNumber} details updated successfully`,
        error: '',
      })
    } catch (err) {
      setStatus({
        loading: false,
        success: '',
        error: err.response?.data?.message || `Unable to update Parent ${parentNumber}`,
      })
    }
  }

  function onChangePrimePlusStatus(i, event) {
    const values = [...primePlusFields]
    values[i][event.target.name] = event.target.value
    setPrimePlusFields(values)
  }

  function handleAddPrimePlus() {
    const values = [...primePlusFields]
    values.push({
      primePlusID: 0,
      startDate: primeFields.primeStartDate || currentDate,
      endDate: primeFields.primeEndDate || currentNextYearDate,
    })
    setPrimePlusFields(values)
  }

  function handleRemovePrimePlus(i) {
    const values = [...primePlusFields]
    values.splice(i, 1)
    setPrimePlusFields(values)
  }
  const [studentCitySuggestions, setStudentCitySuggestions] = useState([])
  const [studentCity, setStudentCity] = useState({ name: '', value: '', stateName: '' })

  const getAllCitiesByNameForS = useCallback(async (cityName) => {
    if (cityName.length >= 3) {
      const result = await axios.get('https://api.collegepass.org/api/v2/utils/citiesByName/' + cityName, {
        headers: {
          authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      // setStudentCitySuggestions(
      return result.data.cities.map((item) => ({
        value: item.id,
        label: `${item.name} - ${item.STATE_NAME}`,
        stateName: item.STATE_NAME,
      }))
      // );
    }
    // setStudentCitySuggestions([]);
    return []
  }, [])

  const findCityById = useCallback(async (value) => {
    try {
      const cityDetails = await axios.get('https://api.collegepass.org/api/v2/utils/citiById/' + value, {
        headers: {
          authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })

      return {
        cityName: cityDetails?.data?.city?.name || '',
        stateName: cityDetails?.data?.city?.state_name || '',
      }
    } catch (err) {
      console.log('Getting city id', err)
      return {
        cityName: '',
        stateName: '',
      }
    }
  }, [])

  const fetchCity = useCallback(
    async (city, isStudent = true) => {
      console.log('Made it here', city, isStudent)

      try {
        if (isStudent) {
          const { cityName, stateName } = city ? await findCityById(city) : { cityName: '', stateName: '' }

          cityName
            ? setStudentCity({
                name: `${cityName} - ${stateName}`,
                value: city,
                stateName,
              })
            : setStudentCity({
                name: 'Bengaluru - Karnataka',
                value: 1558,
                stateName: 'Karnataka',
              })
          city
            ? setStudentCitySuggestions([
                {
                  label: `${cityName} - ${stateName}`,
                  value: city,
                  stateName,
                },
              ])
            : setStudentCitySuggestions([])
        }
      } catch (err) {
        console.log('Error', err)
      }
    },
    [findCityById]
  )

  const getCityById = useCallback(async (id) => {
    try {
      const cityDetails = await axios.get(APIUserCity + '/' + id, {
        headers: {
          authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      if (cityDetails?.data?.data) {
        fetchCity(cityDetails.data.data, true)
      }
    } catch (err) {
      console.log('Getting city id', err)
    }
  }, [])

  useEffect(() => {
    dispatch(listUserBasicDetails(userID))
    dispatch(listUserPrimeDetails(userID))
    dispatch(listUserPrimePlusDetails(userID))
    getCityById(userID)
  }, [dispatch, userID])

  useEffect(() => {
    setMandatoryData({
      userID,
      fName: editUserDetails?.userDetails?.FIRSTNAME,
      lName: editUserDetails?.userDetails?.LASTNAME,
      countryCode: editUserDetails?.userDetails?.COUNTRY_CODE,
      phoneNumber: editUserDetails?.userDetails?.PHONENUMBER,
      grade: editUserDetails?.userDetails?.GRADE,
      curriculum: editUserDetails?.userDetails?.CURRICULUM,
      graduationYear: editUserDetails?.userDetails?.GRADUATION_YEAR,
      mobileOTP: editUserDetails?.userDetails?.MOBILE_OTP || '1',
    })
  }, [editUserDetails.userDetails, userID])

  useEffect(() => {
    setPrimeFields({
      prime: editUserPrimeDetails.userDetails.ISPREMIUM,
      primeStartDate: moment(editUserPrimeDetails.userDetails.PREMIUMSTART).format('YYYY-MM-DD'),
      primeEndDate: moment(editUserPrimeDetails.userDetails.PREMIUMEND).format('YYYY-MM-DD'),
    })
  }, [
    editUserPrimeDetails.userDetails.ISPREMIUM,
    editUserPrimeDetails.userDetails.PREMIUMSTART,
    editUserPrimeDetails.userDetails.PREMIUMEND,
    userID,
  ])

  useEffect(() => {
    if (editUserPrimePlusDetails.userDetails.length > 0) {
      const finPrimePlus = []

      editUserPrimePlusDetails.userDetails.map((primePlus) => {
        const instance = {
          primePlusID: primePlus.ADDON_ID,
          startDate: moment(primePlus.START_DATE).format('YYYY-MM-DD'),
          endDate: moment(primePlus.END_DATE).format('YYYY-MM-DD'),
        }

        finPrimePlus.push(instance)
        return null
      })

      setPrimePlusFields(finPrimePlus)
    }
  }, [editUserPrimePlusDetails.userDetails, userID])

  const onChangePrimeStatus = (e) => {
    const date = new Date(e.target.value)
    date.setDate(date.getDate() + 364)
    if (e.target.name === 'primeStartDate') {
      setPrimeFields({
        ...primeFields,
        [e.target.name]: e.target.value,
        primeEndDate: date.toISOString().split('T')[0],
      })
    } else {
      setPrimeFields({ ...primeFields, [e.target.name]: e.target.value })
    }
  }

  const onChangeMandatoryFields = (e) => {
    e.preventDefault()
    setMandatoryData({ ...mandatoryData, [e.target.name]: e.target.value })
  }

  const isValidDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toString() === 'Invalid Date'
  }

  const onSubmitPrimeStatus = (e) => {
    e.preventDefault()
    // console.log('SUBMIT PRESSED')

    try {
      const primeDetails = {
        id: userID,
        prime: primeFields.prime,
        startDate: primeFields.primeStartDate,
        endDate: primeFields.primeEndDate,
      }

      if (isValidDate(primeDetails.endDate) || isValidDate(primeDetails.startDate)) {
        throw new Error('Invalid Dates')
      }

      dispatch(updateUserPrimeDetails(primeDetails))
      setPrimePlusFields((state) => {
        return state.map((primePlusField) => {
          return { ...primePlusField, startDate: primeDetails.startDate, endDate: primeDetails.endDate }
        })
      })
      setShowPrimeAlert(true)
    } catch (error) {
      setShowPrimeAlert(true)

      dispatch({
        type: UPDATE_PRIME_DETAILS_FAIL,
        payload: {
          status: false,
          error: error.response && error.response.data.message ? error.response.data.message : error.message,
        },
      })
    }
    setTimeout(() => {
      setShowPrimeAlert(false)
      dispatch(clearUpdatePrimeDetailsMessage())
    }, 3000)
  }

  const onSubmitPrimePlusStatus = (e) => {
    e.preventDefault()
    // console.log('SUBMIT PRESSED')
    // let primePlusDetails = [
    // 	{
    // 		primePlusID: 1,
    // 		startDate: '2020-07-03',
    // 		endDate: '2022-07-03',
    // 	},
    // 	{
    // 		primePlusID: 2,
    // 		startDate: '2020-07-03',
    // 		endDate: '2022-07-03',
    // 	},
    // ]
    dispatch(
      updateUserPrimePlusDetails(
        userID,
        primePlusFields.filter((item) => +item.primePlusID !== 0)
      )
    )
    setShowPrimePlusAlert(true)
    setTimeout(() => {
      setShowPrimePlusAlert(false)
      dispatch(clearUpdatePrimePlusDetailsMessage())
    }, 3000)
  }

  const onSubmitMandatoryDetails = (e) => {
    e.preventDefault()
    // {
    // "userID": 8525,
    // "fName": "Chirag",
    // "lName": "Ramachandra",
    // "countryCode": "91",
    // "phoneNumber": 8951831967,
    // "grade": 3,
    // "curriculum": 4,
    // "graduationYear": "2025",
    // "mobileOTP": 1,
    //
    // }

    dispatch(updateUserMandatoryDetails(mandatoryData))
    setShowBasicAlert(true)
    setTimeout(() => {
      setShowBasicAlert(false)
      dispatch(clearBasicDetailsMessage())
    }, 3000)
  }

  const onUpdatePassword = (e) => {
    e.preventDefault()

    if (password.length >= 6) {
      dispatch(updateUserPassword(password, userID))
      setShowPasswordAlert(true)
      setPassword('')
      setPasswordMinErrorText('')
      setTimeout(() => {
        setShowPasswordAlert(false)
        dispatch(clearUpdatePasswordMessage())
      }, 3000)
    } else {
      setShowPasswordAlert(true)
      setPasswordMinErrorText('Entered Password is not 6 characters in length')
    }
  }

  function isValidEmail(emailProvided) {
    // A regular expression that matches the pattern of a valid email address
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailPattern.test(emailProvided)
  }

  const onUpdateEmail = (e) => {
    e.preventDefault()

    if (isValidEmail(email)) {
      dispatch(updateUserEmail(email, userID))
      setShowEmailAlert(true)
      setEmail('')
      setEmailErrorText('')
      setTimeout(() => {
        setShowEmailAlert(false)
        dispatch(clearUpdateEmailMessage())
      }, 3000)
    } else {
      setShowEmailAlert(true)
      setEmailErrorText('Entered Valid Email')
    }
  }

  const onUpdateCity = (e) => {
    e.preventDefault()
    setShowCityAlert(true)

    dispatch(updateUserCity(studentCity.value, userID))

    setTimeout(() => {
      setShowCityAlert(false)
      dispatch(clearUpdateCityMessage())
    }, 3000)
  }

  const PrimePlusFieldsComponents = ({ fields = [] }) => {
    return (
      <div>
        {fields.map((primePlus, idx) => {
          return (
            <Row className="mt-3 p-3 rounded">
              <Col xs={12} sm={12} md={5}>
                <FloatingLabel controlId="primePlusID" label="Premium Plus" className="fs-6">
                  <Form.Select
                    className="bg-grey color-white"
                    aria-label="Floating label select example"
                    defaultValue={primePlus.primePlusID}
                    name="primePlusID"
                    onChange={(e) => onChangePrimePlusStatus(idx, e)}
                  >
                    <option value={0} hidden>
                      Select Plan
                    </option>
                    <option value={1} hidden={primePlusFields.findIndex((item) => +item.primePlusID === 1) >= 0}>
                      SAT - Weekdays
                    </option>
                    <option value={3} hidden={primePlusFields.findIndex((item) => item.primePlusID === 3) >= 0}>
                      Admission Platform
                    </option>
                    <option value={4} hidden={primePlusFields.findIndex((item) => +item.primePlusID === 4) >= 0}>
                      SAT - Saturdays
                    </option>
                    <option value={5} hidden={primePlusFields.findIndex((item) => +item.primePlusID === 5) >= 0}>
                      SAT - Sundays(M)
                    </option>
                    <option value={6} hidden={primePlusFields.findIndex((item) => +item.primePlusID === 6) >= 0}>
                      SAT - Sundays(E)
                    </option>
                    <option value={7} hidden={primePlusFields.findIndex((item) => +item.primePlusID === 7) >= 0}>
                      SAT 1-on-1
                    </option>
                    <option value={10} hidden={primePlusFields.findIndex((item) => +item.primePlusID === 10) >= 0}>
                      AP Classes
                    </option>
                    <option value={13} hidden={primePlusFields.findIndex((item) => +item.primePlusID === 13) >= 0}>
                      Masters
                    </option>
                  </Form.Select>
                </FloatingLabel>
              </Col>

              <Col xs={12} sm={12} md={3}>
                <FloatingLabel controlId="primeStartDate" label="Start Date" className="fs-6">
                  <Form.Control
                    className="bg-grey color-white"
                    type="date"
                    placeholder="Start Date"
                    defaultValue={primePlus.startDate}
                    name="startDate"
                    onChange={(e) => onChangePrimePlusStatus(idx, e)}
                  />
                </FloatingLabel>
              </Col>

              <Col xs={12} sm={12} md={3}>
                <FloatingLabel controlId="primeEndDate" label="End Date" className="fs-6">
                  <Form.Control
                    className="bg-grey color-white"
                    type="date"
                    placeholder="End Date"
                    defaultValue={primePlus.endDate}
                    name="endDate"
                    onChange={(e) => onChangePrimePlusStatus(idx, e)}
                  />
                </FloatingLabel>
              </Col>

              <Col xs={12} sm={12} md={1}>
                <i
                  onClick={() => handleRemovePrimePlus(idx)} // delete button
                  style={{
                    fontSize: '30px',
                    fontWeight: '600',
                    color: '#c93432',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  className="bi bi-trash"
                ></i>
              </Col>
            </Row>
          )
        })}
      </div>
    )
  }

  return (
    <Container fluid>
      <Row className="heading mb-3">
        <h3 className="title">
          <Link to="/">
            <i className="bi bi-arrow-left"></i>
          </Link>
          EDIT USER
        </h3>
      </Row>

      <Row className="mh-100">
        <Col sm={12} md={6} className="mh-100">
          <Card className="text-center edit-user-card-bg color-white ">
            <Card.Body>
              <Card.Title>
                <h3 className="color-white">Mandatory Details</h3>
              </Card.Title>
              <Card.Text>
                <div className="mb-4 color-pink">{editUserDetails.userDetails.EMAIL}</div>
                {/* <p className="text-warning fs-6">
                  Set Mobile OTP to 1, to activate students mobile OTP
                  verification
                </p> */}
              </Card.Text>
              <Form onSubmit={onSubmitMandatoryDetails}>
                <Row>
                  <Col xs={12} sm={12} md={6} className="mb-3">
                    <FloatingLabel
                      controlId="firstName"
                      label="First Name"
                      className={fName ? 'fs-6 color-light-pink' : 'color-light-pink'}
                    >
                      <Form.Control
                        className="bg-grey color-white"
                        type="text"
                        placeholder="First Name"
                        defaultValue={fName}
                        name="fName"
                        onChange={onChangeMandatoryFields}
                      />
                    </FloatingLabel>
                  </Col>
                  <Col xs={12} sm={12} md={6} className="mb-3">
                    <FloatingLabel
                      controlId="lastName"
                      label="Last Name"
                      className={lName ? 'fs-6 color-light-pink' : 'color-light-pink'}
                    >
                      <Form.Control
                        className="bg-grey color-white"
                        type="text"
                        placeholder="Last Name"
                        defaultValue={lName}
                        name="lName"
                        onChange={onChangeMandatoryFields}
                      />
                    </FloatingLabel>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} sm={12} md={6} className="mb-3">
                    <FloatingLabel
                      controlId="countryCode"
                      label="Country Code"
                      className={countryCode ? 'fs-6 color-light-pink' : 'color-light-pink'}
                    >
                      <Form.Control
                        className="bg-grey color-white"
                        type="text"
                        defaultValue={countryCode}
                        placeholder="Country Code"
                        name="countryCode"
                        onChange={onChangeMandatoryFields}
                      />
                    </FloatingLabel>
                  </Col>
                  <Col xs={12} sm={12} md={6} className="mb-3">
                    <FloatingLabel
                      controlId="phoneNumber"
                      label="Phone Number"
                      className={phoneNumber ? 'fs-6 color-light-pink' : 'color-light-pink'}
                    >
                      <Form.Control
                        className="bg-grey color-white"
                        type="text"
                        placeholder="Phone Number"
                        defaultValue={phoneNumber}
                        name="phoneNumber"
                        onChange={onChangeMandatoryFields}
                      />
                    </FloatingLabel>
                  </Col>
                </Row>
                <Row>
                  <Col
                    // xs={12} sm={12} md={6}
                    className="mb-3"
                  >
                    <FloatingLabel
                      controlId="graduationYear"
                      label="Graduation Year"
                      className={graduationYear ? 'fs-6 color-light-pink' : 'color-light-pink'}
                    >
                      <Form.Control
                        className="bg-grey color-white"
                        type="text"
                        defaultValue={graduationYear}
                        placeholder="Graduation Year"
                        name="graduationYear"
                        onChange={onChangeMandatoryFields}
                      />
                    </FloatingLabel>
                  </Col>
                  {/* <Col xs={12} sm={12} md={6} className="mb-3">
                    <FloatingLabel
                      controlId="mobileOTP"
                      label="Mobile OTP"
                      className={
                        mobileOTP ? 'fs-6 color-light-pink' : 'color-light-pink'
                      }
                    >
                      <Form.Control
                        className="bg-grey color-white"
                        type="text"
                        placeholder="Mobile OTP"
                        defaultValue={mobileOTP}
                        name="mobileOTP"
                        onChange={onChangeMandatoryFields}
                      />
                    </FloatingLabel>
                  </Col> */}
                </Row>
                <Row>
                  <Col xs={12} sm={12} md={6} className="mb-3">
                    <FloatingLabel controlId="grade" label="Grade" className={grade ? 'fs-6 color-light-pink' : 'color-light-pink'}>
                      <Form.Select
                        className="bg-grey color-white"
                        aria-label="Floating label select example"
                        // defaultValue={grade}
                        value={grade}
                        name="grade"
                        onChange={onChangeMandatoryFields}
                      >
                        <option value="1">Grade 8</option>
                        <option value="2">Grade 9</option>
                        <option value="3">Grade 10</option>
                        <option value="4">Grade 11</option>
                        <option value="5">Grade 12</option>
                        <option value="12">Year 1</option>
                        <option value="13">Year 2</option>
                        <option value="14">Year 3</option>
                        <option value="15">Year 4</option>
                        <option value="9">Graduated</option>
                        <option value="7">Other</option>
                      </Form.Select>
                    </FloatingLabel>
                  </Col>
                  <Col xs={12} sm={12} md={6} className="mb-3">
                    <FloatingLabel controlId="curriculum" label="Curriculum" className="fs-6 color-light-pink">
                      <Form.Select
                        className="bg-grey color-white"
                        aria-label="Floating label select example"
                        // defaultValue={curriculum}
                        value={curriculum}
                        name="curriculum"
                        onChange={onChangeMandatoryFields}
                      >
                        <option value="0">--Select--</option>
                        <option value="1">IGCSE</option>
                        <option value="2">IB</option>
                        <option value="3">CBSE</option>
                        <option value="4">ICSE</option>
                        <option value="5">STATE BOARD</option>
                        <option value="6">CAIE</option>
                        <option value="7">UNDERGRADUATE / DEGREE</option>
                        <option value="8">OTHER</option>
                      </Form.Select>
                    </FloatingLabel>
                  </Col>
                </Row>
                <Row className="m-0">
                  <Col className="text-center mt-3">
                    <Button type="submit">Update Changes</Button>
                  </Col>
                </Row>
              </Form>

              <Row className="mt-3 px-2">
                {showBasicAlert && updateMandatoryReducer.status === true ? (
                  <Alert variant="success" onClose={() => setShowBasicAlert(false)} dismissible className="rounded">
                    {updateMandatoryReducer.success}
                  </Alert>
                ) : null}
                {showBasicAlert && updateMandatoryReducer.status === false ? (
                  <Alert variant="danger" onClose={() => setShowBasicAlert(false)} dismissible className="rounded">
                    {updateMandatoryReducer.error}
                  </Alert>
                ) : null}
              </Row>
            </Card.Body>
          </Card>
          <Card className="text-center edit-user-card-bg color-white mt-4">
            <Card.Body>
              <Card.Title>
                <h3 className="color-white">User City</h3>
              </Card.Title>

              <Form onSubmit={onUpdateCity}>
                <Row>
                  <Col xs={12} sm={12} md={12} className="mb-3">
                    <AutoSuggest
                      suggestions={studentCitySuggestions}
                      initialValue={{
                        value: studentCity.value,
                        label: studentCity.name,
                      }}
                      fetchSuggestions={getAllCitiesByNameForS}
                      onSelect={(e) => fetchCity(+e.value, true)}
                      placeholder="Select"
                    />
                  </Col>
                </Row>

                <Row className="m-0">
                  <Col className="text-center mt-3">
                    <Button type="submit">Update City</Button>
                  </Col>
                </Row>
                <Row>
                  {showCityAlert && updateCity.status === true ? (
                    <Alert variant="success" onClose={() => setShowCityAlert(false)} dismissible className="rounded">
                      {updateCity.success}
                    </Alert>
                  ) : null}
                  {showCityAlert && updateCity.status === false ? (
                    <Alert variant="danger" onClose={() => setShowCityAlert(false)} dismissible className="rounded">
                      {updateCity.error}
                    </Alert>
                  ) : null}
                </Row>
              </Form>

              <Row className="mt-3 px-2">
                {showBasicAlert && updateMandatoryReducer.status === true ? (
                  <Alert variant="success" onClose={() => setShowBasicAlert(false)} dismissible className="rounded">
                    {updateMandatoryReducer.success}
                  </Alert>
                ) : null}
                {showBasicAlert && updateMandatoryReducer.status === false ? (
                  <Alert variant="danger" onClose={() => setShowBasicAlert(false)} dismissible className="rounded">
                    {updateMandatoryReducer.error}
                  </Alert>
                ) : null}
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Row className="mt-sm-3 mt-md-0">
            <Card className="text-center edit-user-card-bg color-white">
              <Card.Body>
                <Card.Title>
                  <h3 className="color-white">Change Password</h3>
                </Card.Title>
                <Card.Text className="mb-3">
                  {/* <div>{editUserDetails.userDetails.EMAIL}</div> */}
                  <p className="text-warning fs-6 mt-3">
                    Enter atleast 6 characters, there is no validation as of now. Please use this wisely
                  </p>
                </Card.Text>
                <Form onSubmit={onUpdatePassword}>
                  <Row>
                    <Col className="mb-2">
                      <FloatingLabel
                        controlId="password"
                        label="Enter Password"
                        className={password.length > 0 ? 'fs-6 color-light-pink' : 'color-light-pink'}
                      >
                        <Form.Control
                          className="bg-grey color-white"
                          type="text"
                          placeholder="Enter Password"
                          defaultValue={password}
                          name="password"
                          onChange={(e) => {
                            setPassword(e.target.value)
                          }}
                        />
                      </FloatingLabel>
                    </Col>
                  </Row>
                  <Row className="m-0">
                    <Col className="text-center mt-2">
                      <Button type="submit">Update Password</Button>
                    </Col>
                  </Row>
                </Form>
                {showPasswordAlert && updatePassword.status === true ? (
                  <Alert variant="success" onClose={() => setShowPasswordAlert(false)} dismissible className="rounded">
                    {updatePassword.success}
                  </Alert>
                ) : null}
                {showPasswordAlert && updatePassword.status === false ? (
                  <Alert variant="danger" onClose={() => setShowPasswordAlert(false)} dismissible className="rounded">
                    {updatePassword.error}
                  </Alert>
                ) : null}
                {showPasswordAlert && passwordMinErrorText.length > 1 ? (
                  <Alert variant="danger" onClose={() => setShowPasswordAlert(false)} dismissible className="rounded">
                    {passwordMinErrorText}
                  </Alert>
                ) : null}
              </Card.Body>
            </Card>
          </Row>
          <Row className="mt-sm-3 mt-md-0">
            <Card className="text-center edit-user-card-bg color-white mt-3">
              <Card.Body>
                <Card.Title>
                  <h3 className="color-white">Change Email</h3>
                </Card.Title>

                <Form onSubmit={onUpdateEmail}>
                  <Row>
                    <Col className="mb-2">
                      <FloatingLabel
                        controlId="email"
                        label="Enter Email"
                        className={email.length > 0 ? 'fs-6 color-light-pink' : 'color-light-pink'}
                      >
                        <Form.Control
                          className="bg-grey color-white"
                          type="text"
                          placeholder="Enter Email"
                          defaultValue={email}
                          name="email"
                          onChange={(e) => {
                            setEmail(e.target.value)
                          }}
                        />
                      </FloatingLabel>
                    </Col>
                  </Row>
                  <Row className="m-0">
                    <Col className="text-center mt-2">
                      <Button type="submit">Update Email</Button>
                    </Col>
                  </Row>
                </Form>
                {showEmailAlert && updateEmail.status === true ? (
                  <Alert variant="success" onClose={() => setShowEmailAlert(false)} dismissible className="rounded">
                    {updateEmail.success}
                  </Alert>
                ) : null}
                {showEmailAlert && updateEmail.status === false ? (
                  <Alert variant="danger" onClose={() => setShowEmailAlert(false)} dismissible className="rounded">
                    {updateEmail.error}
                  </Alert>
                ) : null}
                {showEmailAlert && emailErrorText.length > 1 ? (
                  <Alert variant="danger" onClose={() => setShowEmailAlert(false)} dismissible className="rounded">
                    {emailErrorText}
                  </Alert>
                ) : null}
              </Card.Body>
            </Card>
          </Row>
          <Row className="mt-3">
            <Card className="text-center edit-user-card-bg color-white">
              <Card.Body>
                <Card.Title className="mb-3">
                  <h3 className="color-white">Prime/ Premium Status</h3>
                </Card.Title>
                <Form onSubmit={onSubmitPrimeStatus}>
                  <Row>
                    <Col className="mb-3">
                      <FloatingLabel controlId="prime" label="Premium" className="fs-6 color-light-pink">
                        <Form.Select
                          className="bg-grey color-white"
                          aria-label="Floating label select example"
                          // defaultValue={
                          //   primeFields.prime === 2 ? 'Premium' : 'Basic'
                          // }
                          value={primeFields.prime}
                          name="prime"
                          onChange={onChangePrimeStatus}
                        >
                          <option value="1">Basic</option>
                          <option value="2">Plus</option>
                          <option value="3">Premium</option>
                          <option value="4">Premium plus</option>
                        </Form.Select>
                      </FloatingLabel>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} sm={12} md={6} className="mb-2">
                      <FloatingLabel controlId="primeStartDate" label="Start Date" className="fs-6 color-light-pink">
                        <Form.Control
                          className="bg-grey color-white"
                          type="date"
                          placeholder="Start Date"
                          // defaultValue={
                          //   primeFields.primeStartDate
                          //     ? primeFields.primeStartDate
                          //     : 'DD-MM-YYYY'
                          // }

                          value={primeFields.primeStartDate ? primeFields.primeStartDate : 'DD-MM-YYYY'}
                          name="primeStartDate"
                          onChange={onChangePrimeStatus}
                        />
                      </FloatingLabel>
                    </Col>
                    <Col xs={12} sm={12} md={6} className="mb-2">
                      <FloatingLabel controlId="primeEndDate" label="End Date" className="fs-6 color-light-pink">
                        <Form.Control
                          className="bg-grey color-white"
                          type="date"
                          placeholder="End Date"
                          // defaultValue={
                          //   primeFields.primeEndDate
                          //     ? primeFields.primeEndDate
                          //     : 'DD-MM-YYYY'
                          // }
                          min={primeFields.primeStartDate ? primeFields.primeStartDate : null}
                          value={primeFields.primeEndDate ? primeFields.primeEndDate : 'DD-MM-YYYY'}
                          name="primeEndDate"
                          onChange={onChangePrimeStatus}
                        />
                      </FloatingLabel>
                    </Col>
                  </Row>
                  <Row className="m-0">
                    <Col className="text-center mt-2">
                      <Button type="submit">Update Changes</Button>
                    </Col>
                  </Row>
                </Form>

                <Row className="mt-3 px-2">
                  {showPrimeAlert && updatePrimeReducer.status === true ? (
                    <Alert variant="success" onClose={() => setShowPrimeAlert(false)} dismissible className="rounded">
                      {updatePrimeReducer.success}
                    </Alert>
                  ) : null}
                  {showPrimeAlert && updatePrimeReducer.status === false ? (
                    <Alert variant="danger" onClose={() => setShowPrimeAlert(false)} dismissible className="rounded">
                      {updatePrimeReducer.error}
                    </Alert>
                  ) : null}
                </Row>
              </Card.Body>
            </Card>
          </Row>
        </Col>
      </Row>
      {parent1 && parent1.id && (
        <ParentDetailsForm
          title="Parent 1"
          parentNumber={1}
          parent={parent1}
          errors={parent1Errors}
          status={parent1Status}
          updateParentField={updateParentField}
          onParentPhoneChange={onParentPhoneChange}
          onParentEmailBlur={onParentEmailBlur}
          saveParent={saveParent}
        />
      )}

      {parent2 && parent2.id && (
        <ParentDetailsForm
          title="Parent 2"
          parentNumber={2}
          parent={parent2}
          errors={parent2Errors}
          status={parent2Status}
          updateParentField={updateParentField}
          onParentPhoneChange={onParentPhoneChange}
          onParentEmailBlur={onParentEmailBlur}
          saveParent={saveParent}
        />
      )}
      <Row className="mt-3">
        <Card className="text-center edit-user-card-bg color-white">
          <Card.Body>
            <Card.Title className="mb-3">
              <h3 className="color-white">Premium Plus Services</h3>
            </Card.Title>
            <Row className="mb-3">
              <Col>
                <Button onClick={() => handleAddPrimePlus()}>Add Plan</Button>
              </Col>
            </Row>

            <PrimePlusFieldsComponents fields={primePlusFields} />

            <Row className="mt-3">
              <Col>
                <Button onClick={onSubmitPrimePlusStatus}>Save</Button>
              </Col>
            </Row>

            {showPrimePlusAlert && updatePrimePlusReducer.status === true ? (
              <Alert variant="success" onClose={() => setShowPrimePlusAlert(false)} dismissible className="rounded">
                {updatePrimePlusReducer.success}
              </Alert>
            ) : null}
            {showPrimePlusAlert && updatePrimePlusReducer.status === false ? (
              <Alert variant="danger" onClose={() => setShowPrimePlusAlert(false)} dismissible className="rounded">
                {updatePrimePlusReducer.error}
              </Alert>
            ) : null}
          </Card.Body>
        </Card>
      </Row>
    </Container>
  )
})
